import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  ButtonGroup,
} from '@mui/material';
import { Close, Info, Refresh, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

const TrackingRecordModal = ({ open, onClose, flowData, isFlowRunning }) => {
  const [trackingRecords, setTrackingRecords] = useState([]);
  const [allTrackingRecords, setAllTrackingRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorRecords, setErrorRecords] = useState([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectedTrackRecord, setSelectedTrackRecord] = useState(null);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [containers, setContainers] = useState([]);
  const [selectedContainerId, setSelectedContainerId] = useState('');
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [sourceDataSearch, setSourceDataSearch] = useState('');
  // Pagination: optional server-side pagination (e.g. 1000 per page)
  const [paginationPage, setPaginationPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 1000;
  const paginationPageRef = useRef(0);

  const token = localStorage.getItem('token');

  useEffect(() => {
    paginationPageRef.current = paginationPage;
  }, [paginationPage]);

  // Helper function to format date (same as in SyncFlowParameter)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // If it's an ISO string, parse it directly
      if (dateString.includes('T') && dateString.includes('Z')) {
        return new Date(dateString).toLocaleString();
      }
      // Otherwise, try to parse it as a regular date
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  const fetchContainers = useCallback(async () => {
    setLoadingContainers(true);
    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-containers`;
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setContainers(data);
    } catch (error) {
      console.error('Error fetching containers:', error);
      setContainers([]);
    } finally {
      setLoadingContainers(false);
    }
  }, [token]);

  const mapRecord = (record, index) => ({
    id: record.id || index,
    sourceData: record.sourceData || 'N/A',
    syncDateTime: record.syncDateTime || new Date().toISOString(),
    conId: record.conId || null,
    message: record.message || (record.status?.toLowerCase().includes('failed') ? 'Record processing failed' : 'No message'),
    status: record.status || 'N/A',
    sourceAppId: record.sourceAppId || 'N/A',
    sourceId: record.sourceId || 'N/A',
    targetAppId: record.targetAppId || 'N/A',
    targetId: record.targetId || 'N/A',
    version: record.version || 'N/A',
    versionDate: record.versionDate || null,
    sflId: record.sflId || 'N/A',
    documentType: record.documentType || 'N/A',
  });

  const fetchTrackingRecords = useCallback(async (showLoading = true, page = 0) => {
    if (!flowData?.instanceId) {
      setAllTrackingRecords([]);
      setTotalCount(0);
      return;
    }
    const instanceId = flowData.instanceId;
    if (showLoading) setLoading(true);
    try {
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-tracks/flow/${instanceId}`;
      const API_URL = `${baseUrl}?instanceId=${instanceId}&page=${page}&size=${PAGE_SIZE}`;

      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Paginated response: { content, total, page, pageSize }
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      const total = isPaginated ? Number(data.total) ?? 0 : list.length;

      const mappedData = list.map((record, index) => mapRecord(record, index));

      setAllTrackingRecords(mappedData);
      setTotalCount(total);
      setPaginationPage(isPaginated ? (data.page ?? page) : 0);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching tracking records:', error);
      if (showLoading) {
        toast.error(`Error loading tracking records: ${error.message}`);
      }
      setAllTrackingRecords([]);
      setTotalCount(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [flowData, token]);

  // Filter tracking records based on selected container and search term
  const filterTrackingRecords = useCallback(() => {
    console.log('Filtering records - selectedContainerId:', selectedContainerId);
    console.log('Search term:', sourceDataSearch);
    console.log('All tracking records:', allTrackingRecords);
    
    let filteredRecords = allTrackingRecords;
    
    // Filter by container if selected
    if (selectedContainerId) {
      filteredRecords = filteredRecords.filter(
        record => record.conId === selectedContainerId
      );
    }
    
    // Filter by source data search term if provided
    if (sourceDataSearch.trim()) {
      const searchTerm = sourceDataSearch.toLowerCase().trim();
      filteredRecords = filteredRecords.filter(record =>
        record.sourceData && record.sourceData.toLowerCase().includes(searchTerm)
      );
    }
    
    console.log('Filtered records:', filteredRecords);
    setTrackingRecords(filteredRecords);
  }, [allTrackingRecords, selectedContainerId, sourceDataSearch]);

  // Apply filtering whenever allTrackingRecords or selectedContainerId changes
  useEffect(() => {
    filterTrackingRecords();
  }, [filterTrackingRecords]);

  // Set up polling when modal opens or flow starts running
  useEffect(() => {
    if (open && flowData) {
      // Initial fetch
      fetchTrackingRecords();
      fetchContainers();

      // Set up polling if flow is running or if we want to check for updates
      if (isFlowRunning || allTrackingRecords.some(record => record.status === 'started')) {
        const interval = setInterval(() => {
          fetchTrackingRecords(false, paginationPageRef.current); // Refresh current page only
        }, 2000); // Poll every 2 seconds

        setPollInterval(interval);

        return () => {
          if (interval) {
            clearInterval(interval);
          }
        };
      }
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, [open, flowData, isFlowRunning, fetchTrackingRecords, fetchContainers]);

  // Stop polling when flow stops running and all records are in final states
  useEffect(() => {
    if (!isFlowRunning && allTrackingRecords.length > 0) {
      const hasActiveRecords = allTrackingRecords.some(record => 
        record.status === 'started' || record.status === 'Running'
      );
      
      if (!hasActiveRecords && pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    }
  }, [isFlowRunning, allTrackingRecords, pollInterval]);

  const fetchErrorRecords = async (trackRecordId) => {
    if (!trackRecordId) return;

    setLoadingErrors(true);
    try {
      const API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-error-records/track/${trackRecordId}/ensure-errors`;

      console.log('Fetching sync error records for track ID:', trackRecordId);

      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch sync error records: ${response.status}`);
        setErrorRecords([]);
        return;
      }

      const data = await response.json();
      console.log('Sync error records response:', data);

      // Ensure data is an array
      const errorRecordsArray = Array.isArray(data) ? data : [];

      // Set the exact error records from sync_error_record table
      setErrorRecords(errorRecordsArray);

      if (errorRecordsArray.length === 0) {
        console.log('No sync error records found for track ID:', trackRecordId);
      } else {
        console.log(`Found ${errorRecordsArray.length} sync error records`);
      }
    } catch (error) {
      console.error('Error fetching sync error records:', error);
      setErrorRecords([]);
    } finally {
      setLoadingErrors(false);
    }
  };

  const handleDetailsClick = (record) => {
    // Show details for failed records, started records, and skipped records
    const failureStatuses = ['failed', 'error', 'validation failed', 'sync failed'];
    const startedStatuses = ['started', 'processing', 'in progress'];
    const skippedStatuses = ['skipped'];
    
    const isFailed = failureStatuses.some(status => record.status?.toLowerCase().includes(status.toLowerCase()));
    const isStarted = startedStatuses.some(status => record.status?.toLowerCase().includes(status.toLowerCase()));
    const isSkipped = skippedStatuses.some(status => record.status?.toLowerCase() === status.toLowerCase());
    
    if (!isFailed && !isStarted && !isSkipped) {
      toast.info('No validation errors to display for successful records');
      return;
    }

    setSelectedTrackRecord(record);
    setErrorModalOpen(true);
    fetchErrorRecords(record.id);
  };

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
    setSelectedTrackRecord(null);
    setErrorRecords([]);
  };

  const handleManualRefresh = () => {
    fetchTrackingRecords(true, paginationPage);
  };

  const handlePrevPage = () => {
    if (paginationPage <= 0) return;
    fetchTrackingRecords(true, paginationPage - 1);
  };

  const handleNextPage = () => {
    if ((paginationPage + 1) * PAGE_SIZE >= totalCount) return;
    fetchTrackingRecords(true, paginationPage + 1);
  };

  const handleContainerChange = (event) => {
    setSelectedContainerId(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSourceDataSearch(event.target.value);
  };

  const handleClose = () => {
    // Clean up polling when modal closes
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
    // Reset container selection, search, and pagination
    setSelectedContainerId('');
    setSourceDataSearch('');
    setPaginationPage(0);
    setTotalCount(0);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'started':
        return 'info';
      case 'integrated':
      case 'completed':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'skipped':
        return 'warning';  // Warning color (orange) for skipped files
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    { 
      field: 'sourceData', 
      headerName: 'Source Data', 
      width: 525,
      headerClassName: 'grid-header'
    },
    { 
      field: 'syncDateTime', 
      headerName: 'Date', 
      width: 200,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <div>
          {formatDate(params.value)}
        </div>
      ),
    },
    { 
      field: 'conId', 
      headerName: 'Container', 
      width: 200,
      headerClassName: 'grid-header',
      renderCell: (params) => {
        const container = containers.find(c => c.conId === params.value);
        return container ? container.conDescription : 'N/A';
      },
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 300,
      headerClassName: 'grid-header',
      renderCell: (params) => {
        const status = params.row.status?.toLowerCase();
        const isFailed = status?.includes('failed') || status?.includes('error');
        const isSkipped = status === 'skipped';
        
        return (
          <div style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            color: isFailed ? '#d32f2f' : isSkipped ? '#ed6c02' : 'inherit',  // Orange for skipped, red for failed
            fontWeight: (isFailed || isSkipped) ? 500 : 'normal'
          }}>
            {params.value || 'No message available'}
          </div>
        );
      },
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 150,
      headerClassName: 'grid-header',
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          color={getStatusColor(params.value)}
          size="small"
          variant={params.value === 'started' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 80,
      headerClassName: 'grid-header',
      renderCell: (params) => {
        const failureStatuses = ['failed', 'error', 'validation failed', 'sync failed'];
        const startedStatuses = ['started', 'processing', 'in progress'];
        const skippedStatuses = ['skipped'];
        
        const hasErrors = failureStatuses.some(status =>
          params.row.status?.toLowerCase().includes(status.toLowerCase())
        );
        
        const isStarted = startedStatuses.some(status =>
          params.row.status?.toLowerCase().includes(status.toLowerCase())
        );
        
        const isSkipped = skippedStatuses.some(status =>
          params.row.status?.toLowerCase() === status.toLowerCase()
        );

        return (hasErrors || isStarted || isSkipped) ? (
          <Tooltip title={
            hasErrors ? "View Validation Errors" : 
            isSkipped ? "View Skip Details" : 
            "View Processing Details"
          }>
            <IconButton
              color={hasErrors ? "error" : isSkipped ? "warning" : "info"}
              onClick={(e) => {
                e.stopPropagation();
                handleDetailsClick(params.row);
              }}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: hasErrors ? 'rgba(211, 47, 47, 0.04)' : 
                                  isSkipped ? 'rgba(237, 108, 2, 0.04)' : 
                                  'rgba(33, 150, 243, 0.04)',
                },
              }}
            >
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="No validation errors (successful record)">
            <IconButton
              disabled
              size="small"
              sx={{
                color: 'text.disabled',
                cursor: 'default',
              }}
            >
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Tracking Records
            {(isFlowRunning || pollInterval) && (
              <Chip
                label="Live Updates"
                color="info"
                size="small"
                sx={{ ml: 2 }}
                icon={<CircularProgress size={16} />}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {flowData?.sfl_mf_name || 'Flow Details'}
            {lastRefresh && (
              <span> • Last updated: {lastRefresh.toLocaleTimeString()}</span>
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={handleManualRefresh}
              disabled={loading}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                },
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'grey.500',
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: 'calc(100% - 80px)' }}>
        {/* Show alert for active polling
        {(isFlowRunning || pollInterval) && (
          <Alert severity="info" sx={{ m: 2, mb: 0 }}>
            Automatically refreshing tracking records every 2 seconds while sync is active...
          </Alert>
        )} */}
        
        {/* Container Filter Dropdown and Search */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="container-filter-label">Filter by Container</InputLabel>
            <Select
              labelId="container-filter-label"
              value={selectedContainerId}
              onChange={handleContainerChange}
              label="Filter by Container"
              disabled={loadingContainers || loading}
            >
              <MenuItem value="">
                <em>All Containers</em>
              </MenuItem>
              {containers.map((container) => (
                <MenuItem key={container.conId} value={container.conId}>
                  {container.conDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            size="small"
            label="Search Source Data"
            placeholder="Enter search term..."
            value={sourceDataSearch}
            onChange={handleSearchChange}
            disabled={loading}
            sx={{ minWidth: 300 }}
          />
        </Box>

        {/* Pagination bar: show only when total > PAGE_SIZE */}
        {totalCount > PAGE_SIZE && (
          <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginationPage * PAGE_SIZE + 1}–{Math.min((paginationPage + 1) * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()} records
            </Typography>
            <ButtonGroup size="small" disableElevation>
              <Button
                startIcon={<NavigateBefore />}
                onClick={handlePrevPage}
                disabled={loading || paginationPage <= 0}
              >
                Previous
              </Button>
              <Button
                endIcon={<NavigateNext />}
                onClick={handleNextPage}
                disabled={loading || (paginationPage + 1) * PAGE_SIZE >= totalCount}
              >
                Next
              </Button>
            </ButtonGroup>
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataGrid
              rows={trackingRecords}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              autoHeight={false}
              sx={{
                flex: 1,
                minHeight: 0,
                '& .grid-header': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f9f9f9',
                },
              }}
            components={{
              NoRowsOverlay: () => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    {isFlowRunning 
                      ? "Sync is running... Records will appear here automatically"
                      : "No tracking records found"
                    }
                  </Typography>
                  {(isFlowRunning || pollInterval) && <CircularProgress size={24} />}
                </Box>
              ),
            }}
          />
          </Box>
        )}
      </DialogContent>

      {/* Error Records Modal - keeping the existing modal unchanged */}
      <Dialog
        open={errorModalOpen}
        onClose={handleCloseErrorModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              {selectedTrackRecord?.status?.toLowerCase() === 'started' || 
               selectedTrackRecord?.status?.toLowerCase() === 'processing' || 
               selectedTrackRecord?.status?.toLowerCase() === 'in progress'
                ? 'Processing Details'
                : selectedTrackRecord?.status?.toLowerCase() === 'skipped'
                ? 'Skip Details'
                : 'Validation Error Records'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedTrackRecord ? `Track ID: ${selectedTrackRecord.id} • Status: ${selectedTrackRecord.status}` : 'Details'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseErrorModal}
            sx={{
              color: 'grey.500',
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {loadingErrors ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : errorRecords.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography color="text.secondary" variant="h6">
                No validation errors found
              </Typography>
              <Typography color="text.secondary" variant="body2">
                This sync track record has no specific validation error records.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <TableContainer component={Paper} sx={{ height: '100%' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                        Message
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>
                        Sync DateTime
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorRecords.map((record) => (
                      <TableRow
                        key={record.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            maxWidth: 400,
                            wordBreak: 'break-word',
                            color: '#d32f2f',
                            fontWeight: 500,
                          }}
                        >
                          {record.message}
                        </TableCell>
                        <TableCell>
                          {record.syncDateTime ? new Date(record.syncDateTime).toLocaleString() : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default TrackingRecordModal;